/**
 * In-place editing state for nested saved-view workspace records (#1144),
 * extracted from LegislationPage: the optimistic patch helper, the editing
 * bundle handed to NestedBillTable/Cards, and the add-note flow.
 *
 * All edits are optimistic with rollback + toast on failure; the server
 * enforces workspace write permission (values gate on canWrite since #1221).
 */

import { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../App';
import { createCustomFieldValue } from '../services/customFieldsApi';
import NotesV2ApiService from '../services/notesV2Api';
import type { Bill } from '../types/bill';
import type { BillWorkspaceRecord } from '../types/savedViews';
import type { CustomFieldDefinition } from '../types/custom-fields';
import type { RecordEditing } from '../components/savedViews/nestedBillCells';

export interface NoteTarget {
  bill: Bill;
  record: BillWorkspaceRecord;
}

export function useWorkspaceRecordEditing({
  setBills,
  customFieldDefs,
}: {
  setBills: React.Dispatch<React.SetStateAction<Bill[]>>;
  customFieldDefs: CustomFieldDefinition[];
}) {
  const [noteTarget, setNoteTarget] = useState<NoteTarget | null>(null);
  const [noteSaving, setNoteSaving] = useState(false);

  /** Patch one workspace record inside the loaded bills (optimistic UI). */
  const patchRecord = useCallback(
    (billId: string, workspaceId: string, patch: Partial<BillWorkspaceRecord>) => {
      setBills((prev) =>
        prev.map((b) =>
          b.id !== billId
            ? b
            : {
                ...b,
                billWorkspaceRecords: (b.billWorkspaceRecords ?? []).map((r) =>
                  r.workspaceId === workspaceId ? { ...r, ...patch } : r
                ),
              }
        )
      );
    },
    [setBills]
  );

  const recordEditing: RecordEditing = useMemo(
    () => ({
      defsById: Object.fromEntries(customFieldDefs.map((d) => [d.id, d])),
      handlers: {
        onPositionChange: async (bill, record, position) => {
          const previous = record.position;
          patchRecord(bill.id, record.workspaceId, { position });
          try {
            await api.put(
              `/workspace-bill-tracking/position/${record.workspaceId}/${bill.id}`,
              { position }
            );
            toast.success(`Position updated in ${record.workspaceName}`);
          } catch (error: any) {
            patchRecord(bill.id, record.workspaceId, { position: previous });
            toast.error(error.response?.data?.message || 'Failed to update position');
          }
        },
        onCustomFieldSave: async (bill, record, fieldDefinitionId, value) => {
          const previous = record.customFieldValues?.[fieldDefinitionId];
          patchRecord(bill.id, record.workspaceId, {
            customFieldValues: { ...record.customFieldValues, [fieldDefinitionId]: value },
          });
          try {
            await createCustomFieldValue({
              field_definition_id: fieldDefinitionId,
              entity_type: 'bill',
              entity_id: bill.id,
              entity_display_name: `${bill.billNumber} - ${bill.title}`,
              workspace_ids: [record.workspaceId],
              value,
            });
            toast.success(`Saved in ${record.workspaceName}`);
          } catch (error: any) {
            patchRecord(bill.id, record.workspaceId, {
              customFieldValues: { ...record.customFieldValues, [fieldDefinitionId]: previous },
            });
            toast.error(error.response?.data?.error || 'Failed to save the field');
          }
        },
        onAddNote: (bill, record) => setNoteTarget({ bill, record }),
      },
    }),
    [customFieldDefs, patchRecord]
  );

  /** NoteFormModal onSave: create in the RECORD's workspace, bump the summary. */
  const saveNote = useCallback(
    async (noteData: unknown) => {
      if (!noteTarget) return;
      setNoteSaving(true);
      try {
        await NotesV2ApiService.createNote(noteTarget.record.workspaceId, noteData as any);
        patchRecord(noteTarget.bill.id, noteTarget.record.workspaceId, {
          notesCount: (noteTarget.record.notesCount ?? 0) + 1,
          lastNoteAt: new Date().toISOString(),
        });
        toast.success(`Note added in ${noteTarget.record.workspaceName}`);
        setNoteTarget(null);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to create the note');
      } finally {
        setNoteSaving(false);
      }
    },
    [noteTarget, patchRecord]
  );

  return { recordEditing, patchRecord, noteTarget, setNoteTarget, noteSaving, saveNote };
}
